import { useEffect, useMemo, useRef } from 'react';
import L from 'leaflet';
import { useT } from '@/store';
import { useOrderDwellRecords, useOrderRouteHistory } from '@/hooks/useOrders';
import { analyseTrail } from '@/lib/routeTrail';
import { SkeletonRows } from '@/components/ui/Panel';
import { formatOrderDateTime } from '@/lib/format';
import { basemapLayer } from '@/lib/basemap';

/**
 * V-72. Where the truck actually was — the breadcrumb behind a dwell charge.
 *
 * GET /api/orders/<id>/route-history has been admin-scoped and tested since
 * the dwell engine shipped, and nothing in frontend/src called it. Its own
 * docstring names the screen it was written for: "so the admin UI can draw
 * the actual path a truck drove, not just its current position". The admin
 * UI never drew it.
 *
 * V-63 put an order's stops on screen — how long, how much over, what it
 * cost. This is the other half of the same conversation: a client on the
 * telephone saying "your truck was not at our site for ninety minutes" is
 * disputing EVIDENCE, and the evidence existed, unreachable, all along.
 * Measured: 692 recorded fixes on one delivery, 380 of them inside the
 * 95-minute stop billed at 48,75 EUR.
 *
 * WHAT THIS DELIBERATELY DOES NOT SAY
 * -----------------------------------
 * It never states a distance driven. Summing the straight line between
 * consecutive fixes overestimates on a straight road (every metre of GPS
 * jitter is added in) and underestimates through a bend, and the resulting
 * number would sit next to the OSRM distance the margin is actually computed
 * from and read as a correction to it. Each fix is a recorded fact; the sum
 * of them is an estimate wearing a measurement's clothes.
 *
 * What it does say beyond drawing the line is where the record is THIN — a
 * gap in the fixes, a trail that stops before the delivery did, a billed stop
 * with nothing recorded inside it. A dispatcher about to defend 48,75 EUR to
 * a client needs to know that before the call, not during it.
 */

// Duplicated from LiveMap.jsx and client/SitePinPicker.jsx, which is the
// convention this codebase already chose twice. Both basemaps are free and
// need no API key.

function TrailMap({ trail }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const trailLayerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return undefined;
    const map = L.map(containerRef.current, { zoomControl: true, attributionControl: true });
    basemapLayer({ dark: true }).addTo(map);
    trailLayerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      trailLayerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const layer = trailLayerRef.current;
    if (!map || !layer || !trail.points.length) return;
    // V-313. useOrderRouteHistory/useOrderDwellRecords refetch (30s
    // staleTime, refetchOnWindowFocus:true — see queryClient.js), which
    // gives `trail` a new object identity while this modal stays mounted.
    // Without clearing first, every redraw ADDED a second copy of the
    // whole trail on top of the first instead of replacing it — the same
    // "redraw without duplicating" problem LiveMap.jsx's geofenceLayerRef
    // solves with the same L.layerGroup() primitive.
    layer.clearLayers();
    const latlngs = trail.points.map((p) => [p.lat, p.lng]);

    // One solid line per RUN of fixes, and a dashed one across each silence.
    // Found in a browser: a single polyline through the whole trail drew an
    // unbroken road across a 42-minute hole, so the picture asserted a drive
    // the text underneath it was busy denying — and the picture is the half
    // that gets screenshotted into an argument with a client.
    for (const run of trail.segments) {
      // A run of ONE fix draws no line, and dropping it silently would take
      // a recorded position off the map — the single fix between two
      // silences is exactly the one worth seeing.
      if (run.length < 2) {
        L.circleMarker([run[0].lat, run[0].lng],
          { radius: 3, color: '#38bdf8', fillOpacity: 0.85 }).addTo(layer);
        continue;
      }
      L.polyline(run.map((p) => [p.lat, p.lng]),
        { color: '#38bdf8', weight: 3, opacity: 0.85 }).addTo(layer);
    }
    for (let i = 1; i < trail.segments.length; i += 1) {
      const from = trail.segments[i - 1][trail.segments[i - 1].length - 1];
      const to = trail.segments[i][0];
      L.polyline([[from.lat, from.lng], [to.lat, to.lng]],
        { color: '#fbbf24', weight: 2, opacity: 0.5, dashArray: '5 7' }).addTo(layer);
    }


    // The two ends of the trail, and every billed stop that has a fix to
    // put it at. A stop with no fix inside its window gets NO marker — an
    // invented one would put a pin on a place nothing recorded.
    L.circleMarker(latlngs[0], { radius: 6, color: '#22c55e', fillOpacity: 1 }).addTo(layer);
    L.circleMarker(latlngs[latlngs.length - 1], { radius: 6, color: '#f43f5e', fillOpacity: 1 }).addTo(layer);
    for (const stop of trail.coverage) {
      if (stop.at) {
        L.circleMarker([stop.at.lat, stop.at.lng], {
          radius: 8, color: '#fbbf24', weight: 2, fillOpacity: 0.25,
        }).addTo(layer);
      }
    }
    map.fitBounds(L.latLngBounds(latlngs), { padding: [18, 18] });
  }, [trail]);

  return <div ref={containerRef} className="h-56 w-full rounded-[11px] border border-line" />;
}

export function OrderRoute({ orderId, order, lang }) {
  const t = useT();
  const { data: fixes, isLoading } = useOrderRouteHistory(orderId);
  const { data: stops } = useOrderDwellRecords(orderId);
  const trail = useMemo(() => analyseTrail(fixes, stops, order ?? {}), [fixes, stops, order]);

  // Nothing was ever going to be recorded for a load nobody is driving, and
  // a panel reporting "no trail" there would be reporting on an absence
  // that is not a gap.
  if (!order?.driver_id) return null;

  return (
    <section>
      <h3 className="panel-title">{t('route.heading')}</h3>
      {isLoading ? <SkeletonRows rows={2} /> : null}

      {!isLoading && trail.fixCount === 0 ? (
        // Said rather than hidden. An empty panel means both "nothing was
        // recorded" and "nobody looked", and the difference is the whole
        // question when a dwell line is disputed.
        //
        // Not role="status": nothing here follows an action the dispatcher
        // just took. It is a standing fact about the load, and announcing it
        // as a live region would also make it the second status in a modal
        // whose Telegram block owns the first.
        <p className="text-[11.5px] leading-snug text-amber">
          {t('route.noneAtAll')}
        </p>
      ) : null}

      {trail.fixCount > 0 ? (
        <div className="space-y-2">
          <TrailMap trail={trail} />
          <p className="text-[11.5px] text-dim">
            {t('route.fixesN', { n: trail.fixCount })}
            {' · '}
            {formatOrderDateTime(trail.firstAt, lang)} – {formatOrderDateTime(trail.lastAt, lang)}
          </p>

          {trail.gaps.length > 0 ? (
            <ul className="space-y-1 text-[11.5px] text-amber">
              {trail.gaps.map((gap) => (
                <li key={gap.fromAt}>
                  {t('route.silenceN', { n: gap.minutes })}
                  {' · '}
                  {formatOrderDateTime(gap.fromAt, lang)} – {formatOrderDateTime(gap.toAt, lang)}
                </li>
              ))}
            </ul>
          ) : null}

          {trail.endsEarlyMinutes !== null ? (
            <p className="text-[11.5px] text-amber">
              {t('route.endsEarlyN', { n: trail.endsEarlyMinutes })}
            </p>
          ) : null}

          {trail.coverage.map((stop) => (
            <p
              key={stop.id}
              className={`text-[11.5px] leading-snug ${stop.fixCount === 0 ? 'text-amber' : 'text-dim'}`}
            >
              {t(`dwell.location.${stop.locationType}`)}
              {': '}
              {stop.fixCount === 0
                ? t('route.stopUnrecorded')
                : t('route.stopFixesN', { n: stop.fixCount })}
            </p>
          ))}
        </div>
      ) : null}
    </section>
  );
}
